// Value formatters allow you to format values for display.

import Big from "big.js";
import { get } from "lodash";

import { BillingDocumentType } from "constants/billingDocuments";
import { getTransferMessage } from "constants/nlis";
import { PricingTypes } from "constants/pricingTypes";
import { VendorCommissionBandTypes } from "constants/vendorCommissionBands";

import { formatAddress } from "lib/address";
import {
  formatDecimal,
  getDollarPriceStringFromCents,
  getDollarsPriceString,
} from "lib";
import { vendorCommissionPercentageFormatter } from "lib/numberFormatters";
import { aggregateText } from "lib/textUtils";
import {
  formatDateString,
  formatDateTimeString,
  formatMonthYearString,
  getAUFormattedDate,
} from "lib/timeFormats";

export const dateFormatter = ({ value }) => getAUFormattedDate(value);

export const localDateTimeFormatter = ({ value }) => {
  if (value == null) {
    return "";
  }
  // When grouped the aggrid doesn't always use the dateGetter,
  // we need to check if the value is a date
  const date = value instanceof Date ? value : new Date(value);
  return formatDateTimeString(date);
};

export const localDateFormatter = ({ value }) => {
  if (value == null) {
    return "";
  }
  // When grouped the aggrid doesn't always use the dateGetter,
  // we need to check if the value is a date
  const date = value instanceof Date ? value : new Date(value);
  return formatDateString(date);
};

export const monthYearFormatter = ({ value }) => {
  if (value == null) {
    return "";
  }
  const date = value instanceof Date ? value : new Date(value);
  return formatMonthYearString(date);
};

const kilogramsFormatter = ({ value }) =>
  typeof value === "number" && value > 0 ? formatDecimal(value, 2) : "";

const unitPriceFormatter = ({ colDef, data, value }) => {
  const saleLot = get(data, colDef.field);

  if (!saleLot?.pricing_type_id) {
    return "";
  }

  const defaultPricingTypeId = get(data, "livestockSale.pricing_type_id");
  const output = [];
  if (defaultPricingTypeId !== saleLot.pricing_type_id) {
    output.push(PricingTypes.toString(saleLot.pricing_type_id));
  }

  const priceString =
    saleLot.pricing_type_id === PricingTypes.PER_KILO
      ? `\xA2 ${value}`
      : `$${value}`;
  output.push(priceString);

  return output.filter(Boolean).join(" ");
};

// This must be used with dollars getter or it will be displayed in cents.
const dollarsFormatter = ({ value }) =>
  value ? `$${getDollarsPriceString(value)}` : "";

// This must be used with dollars getter. Always shows 2 decimal places with thousands separators (e.g., $1,234.00).
const dollarsFormatterWithCents = ({ value }) =>
  value != null ? `$${formatDecimal(value, 2)}` : "";

const percentageFormatter = ({ value }) => (value ? `${value}%` : "-");

function minTwoDecimalPlaces(value) {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  });
}
export const floatFormatter = params => {
  const { column, value } = params;
  const { valueFormatterParams } = column.colDef;
  const {
    decimalPlaces = null,
    defaultValue,
    formatToMinTwoDecimalPlaces = false,
  } = valueFormatterParams || {};
  if (typeof value !== "number") {
    if (typeof defaultValue === "function") {
      return defaultValue(params);
    }
    return null;
  }
  if (typeof decimalPlaces !== "number") {
    return value;
  }

  if (formatToMinTwoDecimalPlaces) {
    return minTwoDecimalPlaces(value);
  }
  return new Big(value).toFixed(decimalPlaces);
};

const nlisTransferMessageFormatter = ({ value }) =>
  value ? getTransferMessage(value) : "";

const picFormatter = ({ value }) => {
  if (!Array.isArray(value)) {
    return null;
  }
  return aggregateText(value, v => v, "PICs");
};
export const dollarTotalsFilterValueGetter = params => {
  const total =
    typeof params.value === "object" ? params.value?.value : params.value;
  return total / 100 || 0;
};
const dollarTotalsFormatter = param => {
  // If the total column is AVG, it returns an object with the min/max and value
  const total =
    typeof param.value === "object" ? param?.value?.value : param?.value;
  return `$${total ? total.toFixed(2) : 0}`;
};

const centsTotalsFormatter = ({ value, colDef }) => {
  if (colDef.valueFormatterParams?.returnDollars && value !== null) {
    return `$${formatDecimal(value / 100)}`;
  }

  return value !== null ? `${value}¢` : "-";
};

const listCountFormatter = ({ value }) => {
  if (value == null) {
    return 0;
  }
  return value.length;
};

const dollarsCreditDebitFormatter = params => {
  if (params?.node.group) {
    return null;
  }
  const billingDocument = params?.node.data.billingDocument ?? {};

  const suffix =
    billingDocument.type === BillingDocumentType.INVOICE ? "Cr" : "Dr";
  const formattedValue = floatFormatter(params);
  return formattedValue ? `${formattedValue} ${suffix}` : "";
};

export function formatIntegrationBusinessAddress(address) {
  return formatAddress({
    addressingInformation: address.addressee,
    country: address.country,
    locality: address.city,
    postalCode: address.postCode,
    route: [address.line1, address.line2].filter(Boolean).join("\n"),
    state: address.state,
  }).trim();
}

export function formatIntegrationBusinessContact(contact) {
  return [
    [contact.firstName, contact.lastName].filter(Boolean).join(" "),
    contact.emailAddress,
    contact.phone,
  ]
    .filter(Boolean)
    .join(", ");
}

export function formatIntegrationBusinessPhone(phone) {
  return [phone.phoneName, phone.phoneNumber].filter(Boolean).join(", ");
}

export function formatIntegrationBusinessCollection(formatter, array) {
  return array
    .map((item, index, arr) => [item, formatter(item, index, arr)])
    .filter(([, itemFormatted]) => Boolean(itemFormatted));
}

export function vendorCommissionBandFormatter(band) {
  if (!band) {
    return "";
  }
  return band.commissionType === VendorCommissionBandTypes.DollarsPerHead
    ? `$${getDollarPriceStringFromCents(band.commissionValue)} per Hd`
    : `${vendorCommissionPercentageFormatter.format(band.commissionValue * 100)}%`;
}

export default {
  centsTotalsFormatter,
  dateFormatter,
  dollarTotalsFormatter,
  dollarsCreditDebitFormatter,
  dollarsFormatter,
  dollarsFormatterWithCents,
  floatFormatter,
  kilogramsFormatter,
  listCountFormatter,
  localDateFormatter,
  localDateTimeFormatter,
  monthYearFormatter,
  nlisTransferMessageFormatter,
  percentageFormatter,
  picFormatter,
  unitPriceFormatter,
  vendorCommissionBandFormatter,
};
